import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, CircularProgress, MenuItem, TextField, } from '@mui/material';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { ApiException, useApi } from '@/hooks/useApi';
import useDebouncedFn from '@/hooks/useDebounce';
import '@/styles/components/CompanySelect.css';
import usePermissions from '@/hooks/usePermissions';
const getFieldCompanyId = (value) => {
    if (!value)
        return null;
    if (typeof value === 'object' && 'id' in value)
        return value.id;
    if (typeof value === 'string')
        return value || null;
    return null;
};
const CompanySelect = ({ children, field, fieldState, formState: _, ownerId, disabled, searchParams, ...inputProps }) => {
    const { t } = useTranslation();
    const { callApi } = useApi();
    const { showError } = useSnackbar();
    const { isKasmAdmin } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState(new Map());
    const endpoint = useCallback((input) => {
        if (!ownerId)
            return '';
        const params = new URLSearchParams(searchParams);
        params.set('owner_id', String(ownerId));
        params.set('page', '1');
        params.set('page_size', '10');
        if (input)
            params.set('search', input);
        return `/companies?${params.toString()}`;
    }, [ownerId, searchParams]);
    const fetchCompanies = useCallback(async (input) => {
        if (!ownerId)
            return;
        try {
            setLoading(true);
            const response = await callApi('GET', endpoint(input));
            if (response) {
                const companies = Object.assign([], response.companies);
                setOptions(prev => {
                    const newMap = new Map(prev);
                    companies.forEach(c => newMap.set(c.id, c));
                    return newMap;
                });
            }
        }
        catch (err) {
            const detail = err instanceof ApiException && err.detail;
            showError(detail || 'companies.messages.loadError');
        }
        finally {
            setLoading(false);
        }
    }, [callApi, endpoint, ownerId, showError]);
    const debouncedFetch = useDebouncedFn(fetchCompanies);
    useEffect(() => {
        const loadInitialCompany = async () => {
            if (!ownerId)
                return;
            if (options.size === 0) {
                const companyId = getFieldCompanyId(field.value);
                if (companyId) {
                    try {
                        setLoading(true);
                        const company = await callApi('GET', `/companies/${companyId}`);
                        if (company) {
                            setOptions(new Map([[company.id, company]]));
                            field.onChange(company);
                        }
                    }
                    catch (err) {
                        console.warn(`Failed to fetch company ${companyId}, falling back to list`, err);
                        fetchCompanies();
                    }
                    finally {
                        setLoading(false);
                    }
                }
                else {
                    fetchCompanies();
                }
            }
        };
        loadInitialCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerId]);
    useEffect(() => {
        if (options.size > 0) {
            setOptions(new Map());
            field.onChange(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerId]);
    const getHubspotId = useCallback((companyId) => {
        return options.get(companyId)?.hubspotId ?? '';
    }, [options]);
    const errorMessage = useMemo(() => {
        return fieldState?.error?.message ?? '';
    }, [fieldState?.error?.message]);
    if (!ownerId) {
        return (_jsx(TextField, { ...inputProps, name: field.name, value: "", disabled: true, label: children, error: !!errorMessage, helperText: errorMessage || t('companies.selectOwnerFirst'), size: "small", sx: {
                '.MuiFormHelperText-root': { marginBottom: '-24px !important' },
            }, slotProps: {
                input: {
                    readOnly: true,
                },
            } }));
    }
    return (_jsx(Autocomplete, { ...field, value: getFieldCompanyId(field.value), disabled: disabled, className: "company-select-form-control", "data-testid": "company-select-form-control", onChange: (_, value) => field.onChange(value !== null ? (options.get(value) ?? null) : null), open: open, onOpen: () => {
            setOpen(true);
        }, onClose: () => setOpen(false), onInputChange: (_, input) => debouncedFetch(input), isOptionEqualToValue: (option, value) => option === value, getOptionLabel: option => options.get(option)?.name ?? '', options: [...options.keys()], loading: loading, renderOption: ({ key, ...props }, id) => (_jsxs(MenuItem, { ...props, className: "company-select-option", "data-testid": "company-select-option", dense: true, children: [options.get(id)?.name, isKasmAdmin && _jsx("small", { children: getHubspotId(id) })] }, key)), renderInput: params => (_jsx(TextField, { ...params, ...inputProps, className: "company-select-text-field", "data-testid": "company-select-text-field", error: !!errorMessage, helperText: errorMessage ||
                (isKasmAdmin ? (field.value?.hubspotId ?? '') : ''), label: children, size: "small", slotProps: {
                // @ts-expect-error data-testid is valid
                formHelperText: { ['data-testid']: 'company-select-helper-text' },
                input: {
                    ...params.InputProps,
                    endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { size: 20 }), params.InputProps.endAdornment] })),
                },
            }, sx: {
                '.MuiFormHelperText-root': { marginBottom: '-24px !important' },
            } })) }));
};
export default CompanySelect;
